import type {
  memberLuckyWheelDrawResponse,
  memberLuckyWheelDrawRecordItem,
  memberLuckyWheelChanceRuleItem,
  memberLuckyWheelPrizeDisplayItem,
  memberLuckyWheelPrizeRecordItem,
  memberLuckyWheelPrizeType,
} from '@/api/member'
import thankYouIcon from '@/assets/raffle/prizes/thank_you.png'
import couponPouchIcon from '@/assets/raffle/prizes/coupon_pouch.png'
import couponTicketsIcon from '@/assets/raffle/prizes/coupon_tickets.png'
import bonusTier1Icon from '@/assets/raffle/prizes/bonus_tier_1.png'
import bonusTier2Icon from '@/assets/raffle/prizes/bonus_tier_2.png'
import bonusTier3Icon from '@/assets/raffle/prizes/bonus_tier_3.png'
import bonusTier4Icon from '@/assets/raffle/prizes/bonus_tier_4.png'
import bonusTier5Icon from '@/assets/raffle/prizes/bonus_tier_5.png'
import bonus88Icon from '@/assets/raffle/prizes/bonus_88lb.png'
import headerWinBonus from '@/assets/raffle/results/header_win_bonus.png'
import headerNoPrize from '@/assets/raffle/results/header_no_prize.png'
import headerWinCoupon from '@/assets/raffle/results/header_win_coupon.png'
import headerFreeChance from '@/assets/raffle/results/header_free_chance.png'

export type LuckyWheelResultVariant = 'win_bonus' | 'no_prize' | 'win_coupon' | 'free_chance'

export interface LuckyWheelResultPopupViewModel {
  headerImage: string
  title: string
  highlightText: string | null
  highlightIsAmount: boolean
  description: string
  primaryText: string
  secondaryText: string | null
}

export const WHEEL_SEGMENT_COUNT = 8
export const WHEEL_SEGMENT_DEG = 360 / WHEEL_SEGMENT_COUNT
/** 扇区中心与顶部指针对齐时的初始校准角 */
export const WHEEL_POINTER_OFFSET = 22.5

export function formatPrizeAmount(amount: string | number): string {
  const num = Number(amount)
  if (Number.isNaN(num)) return String(amount)
  return Number.isInteger(num) ? String(num) : num.toString()
}

export function getPrizeLabel(prize: {
  prize_type: memberLuckyWheelPrizeDisplayItem['prize_type']
  prize_amount: string
}): string {
  if (prize.prize_type === 0) return '谢谢惠顾'
  const amount = formatPrizeAmount(prize.prize_amount)
  if (prize.prize_type === 1) return `${amount}LB彩金`
  return `${amount}LB彩金券`
}

export function getPrizeIcon(prize: memberLuckyWheelPrizeDisplayItem): string {
  if (prize.prize_type === 0) return thankYouIcon
  if (prize.prize_type === 2) {
    const amount = Number(prize.prize_amount)
    return amount <= 2 ? couponTicketsIcon : couponPouchIcon
  }

  const amount = Number(prize.prize_amount)
  if (amount >= 88) return bonus88Icon
  if (amount >= 28) return bonusTier5Icon
  if (amount >= 18) return bonusTier4Icon
  if (amount >= 8) return bonusTier3Icon
  if (amount >= 5) return bonusTier2Icon
  return bonusTier1Icon
}

export function calcWheelTargetRotation(
  wheelIndex: number,
  currentRotation: number,
  spinRounds = 5,
): number {
  const segmentCenter = wheelIndex * WHEEL_SEGMENT_DEG + WHEEL_SEGMENT_DEG / 2
  const targetMod = 360 - segmentCenter + WHEEL_POINTER_OFFSET
  const normalizedCurrent = currentRotation % 360
  const delta = targetMod - normalizedCurrent + spinRounds * 360
  return currentRotation + (delta > spinRounds * 180 ? delta : delta + 360)
}

export function getChanceRuleLabel(ruleType: 1 | 2 | 3): string {
  switch (ruleType) {
    case 1:
      return '完成KYC认证'
    case 2:
      return '每日首次买币'
    case 3:
      return '日常交易'
    default:
      return '活动任务'
  }
}

export function formatActivityDate(timestamp: number): string {
  if (!timestamp) return '--'
  const date = new Date(timestamp * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}`
}

export function formatActivityDateRange(startAt: number, endAt: number): string {
  return `${formatActivityDate(startAt)} ~ ${formatActivityDate(endAt)}`
}

export function getChanceRuleDescription(
  rule: memberLuckyWheelChanceRuleItem,
  coinCode: string,
): string {
  switch (rule.rule_type) {
    case 1:
      return `注册大礼：新用户完成注册即可获得 ${rule.reward_chances} 次抽奖机会。`
    case 2:
      return `首次充值：首次买币金额 ≥ ${rule.condition_amount} ${coinCode} 送 ${rule.reward_chances} 次抽奖机会。`
    case 3:
      return `日常活跃：日常累计卖币 ≥ ${rule.condition_amount} ${coinCode} 送 ${rule.reward_chances} 次抽奖机会。`
    default:
      return ''
  }
}

export function getCouponClaimThreshold(
  rules: memberLuckyWheelChanceRuleItem[],
): string | undefined {
  const tradeRule = rules.find((rule) => rule.rule_type === 2 || rule.rule_type === 3)
  return tradeRule?.condition_amount
}

export const LUCKY_WHEEL_BONUS_EXPIRE_DAYS = 7
export const LUCKY_WHEEL_COUPON_EXPIRE_HOURS = 72

export function getDrawResultVariant(
  prizeType: memberLuckyWheelPrizeType,
): LuckyWheelResultVariant {
  switch (prizeType) {
    case 1:
      return 'win_bonus'
    case 2:
      return 'win_coupon'
    default:
      return 'no_prize'
  }
}

export function getResultPopupViewModel(
  variant: LuckyWheelResultVariant,
  options?: {
    result?: memberLuckyWheelDrawResponse | null
    freeChances?: number
    couponThreshold?: number
  },
): LuckyWheelResultPopupViewModel {
  const result = options?.result
  const freeChances = options?.freeChances ?? 1
  const couponThreshold = options?.couponThreshold ?? 500

  switch (variant) {
    case 'win_bonus':
      return {
        headerImage: headerWinBonus,
        title: '恭喜中奖！',
        highlightText: `${formatPrizeAmount(result?.prize_amount ?? '0')}LB`,
        highlightIsAmount: true,
        description: '彩金已发放至您的彩金余额',
        primaryText: '查看彩金',
        secondaryText: '继续抽奖',
      }
    case 'win_coupon':
      return {
        headerImage: headerWinCoupon,
        title: '恭喜获得彩金券！',
        highlightText: `${formatPrizeAmount(result?.prize_amount ?? '0')} LB`,
        highlightIsAmount: true,
        description: `完成单笔卖币≥${couponThreshold}LB即可兑现彩金`,
        primaryText: '去买币兑现',
        secondaryText: '继续抽奖',
      }
    case 'free_chance':
      return {
        headerImage: headerFreeChance,
        title: '恭喜获得抽奖机会！',
        highlightText: `注册大礼已送达，您获得了 <b class="text-auxiliary-red">${freeChances}</b> 次免费抽奖机会！`,
        highlightIsAmount: false,
        description: '',
        primaryText: '立即抽奖',
        secondaryText: null,
      }
    case 'no_prize':
    default:
      return {
        headerImage: headerNoPrize,
        title: '谢谢惠顾',
        highlightText: '未中奖',
        highlightIsAmount: false,
        description: '下次好运，继续参与活动赢大奖！',
        primaryText: '继续抽奖',
        secondaryText: '返回首页',
      }
  }
}

export function formatDrawRecordTime(timestamp: number): string {
  if (!timestamp) return '--'
  const date = new Date(timestamp * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

export function getDrawRecordPrizeName(item: memberLuckyWheelDrawRecordItem): string {
  if (item.prize_type === 0) return '谢谢参与'
  return `${formatPrizeAmount(item.prize_amount)} LB`
}

export function getDrawRecordStatusLabel(item: memberLuckyWheelDrawRecordItem): string {
  if (item.prize_type === 0) return '谢谢参与'
  if (item.status === 1) return '已发放'
  if (item.status === 2) return '已过期'
  return '待领取'
}

export function isDrawRecordThankYou(item: memberLuckyWheelDrawRecordItem): boolean {
  return item.prize_type === 0
}

export function getBonusRecordTitle(item: memberLuckyWheelPrizeRecordItem): string {
  if (item.status === 2) return '彩金到期'
  if (item.status === 1) return '提取至钱包'
  return '幸运大转盘中奖'
}

export function formatBonusRecordAmount(item: memberLuckyWheelPrizeRecordItem): {
  text: string
  isPositive: boolean
} {
  const amount = formatPrizeAmount(item.prize_amount)
  if (item.status === 0) {
    return { text: `+${amount} LB`, isPositive: true }
  }
  return { text: `-${amount} LB`, isPositive: false }
}

export function formatCouponRecordDateTime(timestamp: number): string {
  if (!timestamp) return '--'
  const date = new Date(timestamp * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function getCouponRecordTitle(item: memberLuckyWheelPrizeRecordItem): string {
  return `${formatPrizeAmount(item.prize_amount)} LB 交易彩金券`
}

export function getCouponRecordStatusLabel(item: memberLuckyWheelPrizeRecordItem): string {
  if (item.status === 1) return '已使用'
  if (item.status === 2) return '已过期'
  return '待使用'
}

export function getCouponRecordStatusClass(item: memberLuckyWheelPrizeRecordItem): string {
  if (item.status === 1) return 'bg-[#BFBFBF] text-white'
  if (item.status === 2) return 'border border-[#FF9500] text-[#FF9500] bg-transparent'
  return 'bg-primary text-white'
}

export function getCouponRecordTimeLabel(item: memberLuckyWheelPrizeRecordItem): string {
  if (item.status === 1) return '使用时间'
  if (item.status === 2) return '过期时间'
  return '有效期至'
}

export function getCouponRecordTimeValue(item: memberLuckyWheelPrizeRecordItem): number {
  if (item.status === 1) return item.used_at
  return item.expires_at
}

export function isCouponRecordInactive(item: memberLuckyWheelPrizeRecordItem): boolean {
  return item.status === 1 || item.status === 2
}

/** 将服务端文案中的次数高亮，如「1 次」 */
export function highlightFreeChanceContent(content: string): string {
  return content.replace(/(\d+\s*次)/, '<b class="text-auxiliary-red">$1</b>')
}

export function parseFreeChancesFromContent(content: string): number {
  const match = content.match(/(\d+)\s*次/)
  return match ? Number(match[1]) : 1
}
